import type {
  MotionExample,
  MotionElement,
  MotionScene,
  MotionCaptionTier,
} from "./types";
const text = (
  id: string,
  content: string,
  props: Partial<MotionElement> = {},
): MotionElement => ({
  id,
  type: "text",
  start: 0,
  end: 6,
  content,
  x: 90,
  y: 500,
  font: "Montserrat",
  size: 80,
  weight: 800,
  fill: "#ffffff",
  maxWidth: 900,
  ...props,
});
const scene = (
  elements: MotionElement[],
  background?: MotionScene["background"],
): MotionScene => ({
  version: 1,
  ...(background ? { background } : {}),
  elements,
});
const captionExample = (
  id: string,
  name: string,
  tiers: MotionCaptionTier[],
  content: string[],
  animation: Partial<MotionElement> = {},
): MotionExample => ({
  id,
  name,
  scene: scene([
    {
      id: "captions",
      type: "captions",
      start: 0,
      end: 6,
      x: 100,
      y: 650,
      maxWidth: 880,
      align: "center",
      wordsPerCue: content.length,
      tiers,
      words: content.map((word, index) => ({
        text: word,
        start: index * 0.65,
        end: index * 0.65 + 0.6,
        tier: tiers[index % tiers.length].id,
      })),
      ...animation,
    },
  ]),
});
const authoredExamples: MotionExample[] = [
  {
    id: "lower-third",
    name: "Lower third",
    scene: scene([
      {
        id: "panel",
        type: "rect",
        start: 0,
        end: 6,
        x: 80,
        y: 1250,
        width: 900,
        height: 210,
        radius: 24,
        fill: "#101827ee",
        enter: { type: "slide", direction: "left", duration: 0.4 },
        exit: { type: "fade", duration: 0.4 },
      },
      text("name", "MARIA SILVA", {
        y: 1280,
        size: 72,
        enter: { type: "fade", duration: 0.6 },
      }),
      text("role", "Criadora de conteúdo", {
        y: 1370,
        size: 38,
        weight: 500,
        fill: "#a8b4d0",
      }),
    ]),
  },
  {
    id: "title-card",
    name: "Title card",
    scene: scene(
      [
        text("headline", "UMA IDEIA\nMUDA TUDO", {
          y: 650,
          size: 110,
          align: "center",
          enter: { type: "pop", duration: 0.8 },
        }),
      ],
      {
        type: "linear",
        angle: 135,
        stops: [
          { offset: 0, color: "#142030" },
          { offset: 1, color: "#4230a0" },
        ],
      },
    ),
  },
  {
    id: "subscribe",
    name: "Subscribe CTA",
    scene: scene([
      {
        id: "button",
        type: "rect",
        start: 0,
        end: 6,
        x: 230,
        y: 1000,
        width: 620,
        height: 160,
        radius: 80,
        fill: "#fa3636",
        loops: [{ type: "pulse", amount: 0.04, duration: 2 }],
      },
      text("cta", "INSCREVA-SE", { x: 300, y: 1050, size: 58 }),
      {
        id: "bell",
        type: "icon",
        start: 0,
        end: 6,
        name: "Bell",
        x: 780,
        y: 1040,
        width: 60,
        height: 60,
        color: "#ffffff",
        loops: [{ type: "wiggle", amount: 10, duration: 1.5 }],
      },
    ]),
  },
  {
    id: "counter",
    name: "Counter",
    scene: scene([
      text("counter", "", {
        y: 700,
        size: 180,
        countUp: {
          from: 0,
          to: 100000,
          decimals: 0,
          prefix: "R$ ",
          locale: "pt-BR",
        },
        enter: { type: "fade", duration: 0.4 },
      }),
    ]),
  },
  {
    id: "kinetic-captions",
    name: "Kinetic type",
    scene: scene([
      text("kinetic", "Crie algo incrível", {
        size: 100,
        split: "word",
        stagger: 0.16,
        enter: { type: "slide", direction: "up", duration: 0.4 },
      }),
    ]),
  },
  {
    id: "emoji-burst",
    name: "Emoji burst",
    scene: scene([
      {
        id: "burst",
        type: "particles",
        start: 0,
        end: 6,
        x: 540,
        y: 1000,
        seed: 42,
        count: 32,
        shape: "emoji",
        emoji: "🎉",
        lifetime: 3,
        speed: 380,
        spread: 360,
        gravity: 220,
        spin: 180,
        colors: ["#ffca28", "#ff4081", "#55ccff"],
        sizes: [30, 50, 70],
        fadeOut: true,
      },
    ]),
  },
  {
    id: "bar-chart",
    name: "Bar chart",
    scene: scene(
      [0, 1, 2].map((i) => ({
        id: `bar${i}`,
        type: "rect",
        start: 0,
        end: 6,
        x: 180 + 250 * i,
        y: 750,
        width: 160,
        height: 600,
        fill: ["#6266ee", "#31d0b0", "#ffca28"][i],
        anchor: { x: 0, y: 1 },
        keyframes: {
          scaleY: [
            { t: 0, v: 0 },
            { t: 1 + i * 0.2, v: [0.4, 0.7, 1][i], ease: "ease-out" },
          ],
        },
      })),
    ),
  },
  {
    id: "route",
    name: "Route drawing",
    scene: scene([
      {
        id: "route",
        type: "path",
        start: 0,
        end: 6,
        d: "M100 1200 C200 700 600 1400 950 500",
        stroke: "#55e0c0",
        strokeWidth: 16,
        lineCap: "round",
        enter: { type: "draw", duration: 3 },
      },
      {
        id: "marker",
        type: "ellipse",
        start: 0,
        end: 6,
        width: 44,
        height: 44,
        fill: "#ffffff",
        followPath: {
          d: "M100 1200 C200 700 600 1400 950 500",
          progress: [
            { t: 0, v: 0 },
            { t: 3, v: 1 },
          ],
          autoRotate: false,
        },
      },
    ]),
  },
  {
    id: "repeated-stars",
    name: "Repeater",
    scene: scene([
      {
        id: "star",
        type: "polygon",
        start: 0,
        end: 6,
        x: 100,
        y: 800,
        width: 120,
        height: 120,
        sides: 5,
        innerRadius: 0.45,
        fill: "#ffcc44",
        repeat: { count: 6, x: 160, rotation: 10, delay: 0.12 },
        enter: { type: "pop", duration: 0.4 },
      },
    ]),
  },
  {
    id: "group-3d",
    name: "Clipped 3D group",
    scene: scene([
      {
        id: "group",
        type: "group",
        start: 0,
        end: 6,
        x: 200,
        y: 650,
        width: 680,
        height: 420,
        clip: true,
        perspective: 1200,
        keyframes: {
          rotateY: [
            { t: 0, v: -20 },
            { t: 3, v: 20 },
            { t: 6, v: -20 },
          ],
        },
        children: [
          {
            id: "group-bg",
            type: "rect",
            start: 0,
            end: 6,
            width: 680,
            height: 420,
            radius: 40,
            fill: "#4535bb",
          },
          text("group-label", "PERSPECTIVA", { x: 40, y: 150, size: 66 }),
        ],
      },
    ]),
  },
  captionExample(
    "kosky1",
    "Kosky 1 · yellow headline / white supporting line",
    [
      {
        id: "headline",
        font: "Anton",
        size: 114,
        weight: 900,
        fill: "#faff00",
        transform: "uppercase",
        align: "center",
        stroke: "#101010",
        strokeWidth: 2,
      },
      {
        id: "support",
        font: "Montserrat",
        size: 45,
        weight: 600,
        fill: "#ffffff",
        align: "center",
      },
    ],
    ["NINGUÉM", "acredita", "NO", "começo"],
    { cueEnter: { type: "fade", duration: 0.15 }, cueExit: { type: "fade", duration: 0.12 } },
  ),
  captionExample(
    "kosky2",
    "Kosky 2 · green sans / white headline / green italic serif",
    [
      {
        id: "small",
        font: "Montserrat",
        size: 34,
        weight: 600,
        fill: "#b6fa11",
        transform: "uppercase",
        align: "center",
      },
      {
        id: "headline",
        font: "Anton",
        size: 120,
        weight: 900,
        fill: "#ffffff",
        transform: "uppercase",
        align: "center",
      },
      {
        id: "serif",
        font: "Georgia",
        size: 72,
        italic: true,
        fill: "#b6fa11",
        align: "center",
      },
    ],
    ["QUANDO", "VOCÊ", "acredita", "TUDO", "MUDA", "de verdade"],
    { cueEnter: { type: "fade", duration: 0.2 }, cueExit: { type: "fade", duration: 0.15 } },
  ),
  captionExample(
    "kosky3",
    "Kosky 3 · gray italic / mint glow",
    [
      {
        id: "gray",
        font: "Montserrat",
        size: 68,
        weight: 900,
        italic: true,
        fill: "#d4d4d4",
        transform: "uppercase",
        align: "center",
      },
      {
        id: "mint",
        font: "Montserrat",
        size: 92,
        weight: 900,
        italic: true,
        fill: "#8dffdf",
        transform: "uppercase",
        align: "center",
        shadow: { color: "#77ffd4", blur: 18, x: 0, y: 0 },
      },
    ],
    ["VAI", "CONSEGUIR", "ACREDITE", "EM VOCÊ"],
    { cueEnter: { type: "fade", duration: 0.15 }, wordEnter: { type: "blur", duration: 0.25 } },
  ),
];

// Persist public font assets in examples too: the gallery/headless renders must
// not depend on the legacy font metadata service or a local catalog.
const exampleFontUrls: Record<string, string> = {
  Montserrat: 'https://fonts.gstatic.com/s/montserrat/v31/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Ew-.ttf',
  Anton: 'https://fonts.gstatic.com/s/anton/v27/1Ptgg87LROyAm0K0.ttf',
};
const withExampleFonts = (element: MotionElement): MotionElement => ({
  ...element,
  ...(element.font && exampleFontUrls[element.font] ? { fontUrl: exampleFontUrls[element.font] } : {}),
  ...(element.tiers ? { tiers: element.tiers.map(tier => ({
    ...tier, ...(tier.font && exampleFontUrls[tier.font] ? { fontUrl: exampleFontUrls[tier.font] } : {}),
  })) } : {}),
  ...(element.children ? { children: element.children.map(withExampleFonts) } : {}),
});
export const motionExamples: MotionExample[] = authoredExamples.map(example => ({
  ...example, scene: { ...example.scene, elements: example.scene.elements.map(withExampleFonts) },
}));
